import { useNavigate } from 'react-router-dom';
import { ArrowRight, Gamepad2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { ArcheEngineLanding } from '../../../components/ArcheEngineLanding';

const ARCHE_ID = 'arche_crafting';
/** Bare LitCraft game arche (plugin); opened as a best-effort link. */
const LITCRAFT_PATH = '/a/litcraft';

/**
 * Landing screen for the Crafting arche.
 *
 * This arche is the recipe-and-crafting system behind every LitCraft game, not a
 * user-facing app. End users (and admins) see a friendly "nothing to see here"
 * screen. Admins/litcraft_admins additionally get the Admin entry point at the
 * bottom — the "nothing to see here" copy is shown to admins too, deliberately,
 * so they can confirm exactly what a normal player sees.
 *
 * Admin data itself is protected server-side (`requireAdminOrLitcraftAdmin`); this
 * gate is presentational defense-in-depth, not the security boundary.
 */
export function CraftingLandingPage() {
  const navigate = useNavigate();
  // Server route gate: `requireAdminOrLitcraftAdmin` (docs/api-admin.md).
  const isAdmin = useArcheAdmin('litcraft_admin');

  return (
    <ArcheEngineLanding
      archeId={ARCHE_ID}
      title="Crafting"
      description={
        <>
          This is the crafting system behind all the games you love in the LitCraft universe — the
          recipes and creations that power your adventures. There's nothing for you to do on this
          page.
        </>
      }
      actions={
        <Button onClick={() => navigate(LITCRAFT_PATH)} className="gap-2">
          <Gamepad2 className="h-4 w-4" />
          Open LitCraft
        </Button>
      }
      footer={
        isAdmin && (
          <Card
            className="w-full mt-8 hover:bg-muted/50 transition-colors cursor-pointer text-left"
            onClick={() => navigate(`/a/${ARCHE_ID}/admin`)}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Admin</p>
                  <p className="text-sm text-muted-foreground">
                    Manage recipes, learned recipes, and the crafting log
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        )
      }
    />
  );
}
